import React, { useCallback, useState } from "react";
import { styled } from "@mui/material/styles";
import { Checkbox } from "@mui/material";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";

import { CaseMetaType } from "interfaces/case-meta-type";
import { CaseMetaFilter as OptionType } from "interfaces/case-filter";
import { Autocomplete } from "components/Autocomplete";
import { useLazyGetCaseMetaTypes } from "hooks/case-meta-types/useLazyGetCaseMetaTypes";
import { filterOptions } from "components/CasesTable/Filters/utils/filterOptions";

interface DelayReasonFilterProps {
  selectedDelayReasons: CaseMetaType[];
  onChangeSelectedDelayReasons: (values: CaseMetaType[]) => void;
  showChips?: boolean;
}

const selectAllOption: CaseMetaType = {
  id: -1,
  name: "Select All",
  data_type: 0,
  data_values: [],
  data_value_details: [],
  change_date: "",
  change_user: 0,
  create_date: "",
  create_user: 0
};

export const DelayReasonFilter = ({
  selectedDelayReasons,
  onChangeSelectedDelayReasons,
  showChips = true
}: DelayReasonFilterProps): JSX.Element => {
  const {
    loading: priorityLoading,
    error: priorityError,
    fetchCaseMetaTypes: getMetaTypes
  } = useLazyGetCaseMetaTypes();
  const [options, setOptions] = useState<OptionType[] | undefined>(undefined);
  const [selectedState, setSelectedState] = useState<
    (CaseMetaType | OptionType)[]
  >([]);
  const [inputValue, setInputValue] = useState("");

  const onFocus = useCallback(() => {
    /** If it is the first focus event, get options. */
    if (options === undefined) {
      getMetaTypes("name=Delay%20Reason").then(response => {
        if (response.results) {
          /** Metatype */
          const formattedOptions: OptionType[] = [];
          response.results.forEach(option => {
            option.data_value_details?.forEach(detail => {
              formattedOptions.push({ id: detail.name, name: detail.name });
            });
          });
          setOptions([selectAllOption, ...formattedOptions]);
        }
      });
    }
    setSelectedState(selectedDelayReasons);
  }, [options, getMetaTypes, selectedDelayReasons]);

  return (
    <StyledAutocomplete
      label="Delay Reason"
      placeholder={showChips ? "Delay Reason" : ""}
      loading={priorityLoading}
      error={priorityError}
      showChips={showChips}
      size="small"
      multiple
      autoHighlight
      disableCloseOnSelect
      disableClearable
      autoComplete
      onFocus={onFocus}
      options={options ?? []}
      noOptionsText="No results found"
      getOptionLabel={(option: OptionType) => option.name}
      isOptionEqualToValue={(option: OptionType, value: OptionType) =>
        option.name === value.name
      }
      value={selectedDelayReasons}
      onInputChange={(
        _event: React.ChangeEvent,
        newInputValue: string
      ): void => {
        if (inputValue !== newInputValue) {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      renderOption={(
        props: any,
        option: OptionType,
        { selected }: { selected: any }
      ) => {
        let { key, ...optionProps } = props;
        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");
        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );
        const isSelected =
          option.name.toLowerCase() === "select all"
            ? isEqual(filteredSelectedState, filteredNonSelectAllOptions)
            : selected;

        optionProps = {
          ...optionProps,
          "aria-selected": isSelected
        };

        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={isSelected} />
            {option.name}
          </li>
        );
      }}
      onChange={(_event: React.ChangeEvent, values: OptionType[]) => {
        if (!options?.length) {
          return;
        }

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );
        if (values.some(value => value.name.toLowerCase() === "select all")) {
          // If current selection includes "Select All"
          if (isEqual(filteredSelectedState, filteredNonSelectAllOptions)) {
            // If everything was selected, deselect all
            const selected = selectedState.filter(
              option =>
                !filteredSelectedState.some(
                  selected => selected.id === option.id
                )
            );
            onChangeSelectedDelayReasons(selected as any);
            setSelectedState(selected);
          } else {
            const selected = uniqBy(
              [...selectedDelayReasons, ...filteredNonSelectAllOptions],
              "id"
            );

            // If not everything was selected, select all
            onChangeSelectedDelayReasons(selected as any);
            setSelectedState(selected);
          }
        } else {
          // Normal selection of individual items
          onChangeSelectedDelayReasons(values as any);
          setSelectedState(values);
        }
      }}
    />
  );
};

const StyledAutocomplete = styled(Autocomplete)`
  height: 100%;
`;
